import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Divider,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BuildIcon from '@mui/icons-material/Build';
import EditIcon from '@mui/icons-material/Edit';

import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useGetServiceByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { buildServiceRelationParams } from './serviceApiHelpers';

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const ViewService = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetServiceByIdQuery(
    { id, params: buildServiceRelationParams() },
    { skip: !id }
  );

  const service = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || 'Failed to load service');
      navigate('/services');
    }
  }, [isError, error, navigate]);

  if (loading || !service) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const isNumeric = (v) => v !== null && v !== undefined && v !== '';

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Top actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate('/services')}
          >
            Back
          </Button>

          <Can permission="service-edit">
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              onClick={() => navigate(`/services/edit/${id}`)}
            >
              Edit Service
            </Button>
          </Can>
        </Box>

        {/* HEADER CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'divider',
            background:
              'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <BuildIcon />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {service.name ?? 'Service'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Service configuration & pricing details
              </Typography>
            </Box>

            <Chip
              label={service.status?.name ?? service.status?.label ?? 'Active'}
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Paper>

        {/* MAIN GRID */}
        <Grid container spacing={3}>

          {/* PRICING CARD */}
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Pricing
                </Typography>

                <Stack spacing={2}>
                  <InfoItem label="Internal Charges" value={service.internal_charges} />
                  <InfoItem label="External Charges" value={service.external_charges} />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  Margin
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {isNumeric(service.external_charges) && isNumeric(service.internal_charges)
                    ? Number(service.external_charges) - Number(service.internal_charges)
                    : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* DETAILS CARD */}
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoItem label="Name" value={service.name} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label="Status"
                      value={
                        service.status?.name ??
                        service.status?.label ??
                        service.status_name
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem
                      label="Author"
                      value={service.author?.name ?? service.author?.username}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InfoItem label="Created At" value={service.created_at} />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />

                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', mt: 1 }}
                    >
                      {service.description ?? '—'}
                    </Typography>
                  </Grid>
                </Grid>

              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </PageLayout>
  );
};

export default ViewService;